import { faExclamationTriangle, faPen } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { SubmitHandler, useFormContext } from "react-hook-form";
import { Button, Form, Message, Segment } from "semantic-ui-react";
import AccessibleCheckbox from "../../../../../../components/global_components/AccessibleCheckbox";
import { useZonedDateTime } from "../../../../../../components/global_components/ZonedDateTime";
import { pluralize } from "../../../../../../utils/pluralize";
import { useNotificationInfo } from "../../../../hooks/useNotificationInfo";
import { NotifyNotificationsStatusLabelType } from "../pages/notifications/components/NotifyNotificationsStatusLabel";
import { GeoTarget } from "./ControlledGeoTargetToggle";
import { NotifyNotificationsChannels } from "./ControlledNotifyNotificationsChannels";
import { NotifyNotificationEstimatedReach } from "./NotifyNotificationEstimatedReach";
import { NotifyNotificationsFormData } from "./NotifyNotificationsForm";
import { NotifyNotificationsPreview } from "./NotifyNotificationsPreview";

export function NotifyNotificationsReview({
  contentStatus,
  onReturnToEditStep,
  isSaveDraftLoading,
  onSaveDraft,
  isSendLoading,
  onSend,
  isScheduleLoading,
  onSchedule,
}: {
  contentStatus: NotifyNotificationsStatusLabelType;
  onReturnToEditStep: () => void;
  onSaveDraft: SubmitHandler<NotifyNotificationsFormData>;
  isSaveDraftLoading: boolean;
  onSend: SubmitHandler<NotifyNotificationsFormData>;
  isSendLoading: boolean;
  onSchedule: SubmitHandler<NotifyNotificationsFormData>;
  isScheduleLoading: boolean;
}) {
  const { handleSubmit, watch } = useFormContext<NotifyNotificationsFormData>();
  const { voiceTranscription, channels, sendAt, archiveAt, geoTargetFeatures, fromDepartment, subscriptionGroups } =
    watch();

  const [confirmCost, setConfirmCost] = useState(false);

  const { billing } = useNotificationInfo({
    voiceContent: voiceTranscription,
    voiceForceSendAll: false,
    channels: {
      sms: !!channels.active.find((c) => c === "Sms"),
      voice: !!channels.active.find((c) => c === "Voice"),
    },
  });

  const hasUnconfirmedCost = confirmCost === false && !!billing.cost;
  const shouldDisablePublishButtons = hasUnconfirmedCost || billing.willOverdraft || isSendLoading || isScheduleLoading;

  const statusIsDraft = contentStatus === NotifyNotificationsStatusLabelType.Draft;
  const statusIsPending = contentStatus === NotifyNotificationsStatusLabelType.Pending;

  const showGeoTargeting = !!geoTargetFeatures?.featureIds.length;
  const showSendAt = !!sendAt;
  const sendAtAlreadyPast = sendAt ? sendAt < new Date() : false;
  const showSendAtAlreadyPassedWarning = showSendAt && sendAtAlreadyPast && (statusIsDraft || statusIsPending);
  const showArchiveAt = !!archiveAt;
  const showSchedule = showSendAt || showArchiveAt;
  const showConfirmCost = !!billing.cost && !billing.willOverdraft;
  const showOverdraftWarning = billing.willOverdraft;

  const shouldSchedule = !!sendAt;
  const shouldSend = !shouldSchedule;

  const zonedSendAt = useZonedDateTime({
    value: sendAt,
    formatString: "MMM do, yyyy 'at' h:mm aa",
  });
  const zonedArchiveAt = useZonedDateTime({
    value: archiveAt,
    formatString: "MMM do, yyyy 'at' h:mm aa",
  });

  return (
    <div className='tw-mx-gg-3 tw-mb-7 tw-mt-7 md:tw-mx-7 lg:tw-mb-0 lg:tw-h-[calc(100vh-200px)]'>
      <Segment className='tw-h-full tw-w-full lg:tw-pr-gg-4'>
        <div className='tw-flex tw-h-full tw-w-full tw-flex-col tw-gap-gg-5 lg:tw-flex-row lg:tw-gap-gg-4'>
          <NotifyNotificationsPreview height={`100%`} />
          <div className='tw-w-full lg:tw-overflow-y-auto lg:tw-px-gg-2'>
            <NotifyNotificationEstimatedReach hasTransparentBackground />
            <div className='tw-space-y-6 tw-pt-gg-5'>
              <Form className='tw-space-y-3'>
                <div className='tw-flex tw-items-start tw-justify-between tw-gap-3'>
                  <Form.Field className='tw-w-full'>
                    <label>Sending Group</label>
                    <span className='tw-text-text-secondary'>
                      {fromDepartment?.label || "No Sending Group Selected"}
                    </span>
                  </Form.Field>
                  <ReturnToEditButton onClick={onReturnToEditStep} />
                </div>
                <div className='tw-flex tw-items-start tw-justify-between tw-gap-3'>
                  <Form.Field className='tw-w-full'>
                    <label>Subscription Groups</label>
                    <span className='tw-text-text-secondary'>
                      {subscriptionGroups?.map((group) => group.label).join(" • ")}
                      {!subscriptionGroups?.length && "No Subscription Groups Selected"}
                    </span>
                  </Form.Field>
                  <ReturnToEditButton onClick={onReturnToEditStep} />
                </div>
                {showGeoTargeting && (
                  <div className='tw-mb-3 tw-flex tw-items-start tw-justify-between tw-gap-3'>
                    <Form.Field className='tw-w-full'>
                      <GeoTarget
                        value={{
                          featureIds: geoTargetFeatures?.featureIds || [],
                          restrictBoundary: geoTargetFeatures?.restrictBoundary || false,
                        }}
                        readonly
                      />
                    </Form.Field>
                    <ReturnToEditButton onClick={onReturnToEditStep} />
                  </div>
                )}
                <div className='tw-flex tw-items-start tw-justify-between tw-gap-3'>
                  <Form.Field className='tw-w-full'>
                    <label>Channels</label>
                    <span className='tw-text-text-secondary'>
                      <NotifyNotificationsChannels value={channels} readonly />
                    </span>
                  </Form.Field>
                  <ReturnToEditButton onClick={onReturnToEditStep} />
                </div>

                {showSchedule && (
                  <div className='tw-flex tw-items-start tw-justify-between tw-gap-3'>
                    <Form.Field className='tw-w-full'>
                      <label className='tw-leading-4'>Schedule</label>
                      <span className='tw-text-text-secondary'>
                        {showSendAt && <div>Send: {zonedSendAt}</div>}
                        {showArchiveAt && <div>Expires: {zonedArchiveAt}</div>}
                      </span>
                    </Form.Field>
                    <div className='tw-flex tw-flex-col-reverse tw-items-end tw-gap-2 sm:tw-flex-row sm:tw-gap-3'>
                      {showSendAtAlreadyPassedWarning && (
                        <div className='tw-flex tw-items-center tw-gap-2 tw-leading-4 tw-text-danger-dark'>
                          <FontAwesomeIcon icon={faExclamationTriangle} />
                          <span className='tw-whitespace-nowrap'>Send date already passed</span>
                        </div>
                      )}
                      <ReturnToEditButton onClick={onReturnToEditStep} />
                    </div>
                  </div>
                )}
              </Form>

              <div>
                {showConfirmCost && (
                  <div className='tw-flex tw-gap-2 tw-bg-warning-background tw-p-5 tw-text-text-warning'>
                    <AccessibleCheckbox
                      label={`Confirm this message will use an estimated ${billing.cost ?? 0} ${pluralize(
                        billing.cost,
                        "credit",
                        "credits",
                      )} out of ${billing.available ?? 0} credit balance`}
                      checked={confirmCost}
                      onChange={(checked) => setConfirmCost(checked)}
                      warning
                    />
                  </div>
                )}
                {showOverdraftWarning && (
                  <Message negative>
                    <p>
                      <Message.Header>Insufficient Credits</Message.Header>
                      <Message.Content>
                        This message will overdraft your account by {Math.abs(billing.balance ?? 0)}{" "}
                        {pluralize(billing.balance ?? 0, "credit", "credits")} and cannot be sent.
                      </Message.Content>
                      <br />
                      <Message.Content>Purchase more credits by contacting GOGov support.</Message.Content>
                      <Message.Content>
                        <a href='mailto:Support@GOGovApps.com'>Support@GOGovApps.com</a>
                      </Message.Content>
                      <Message.Content>
                        <a href='tel:+19254564926'>(925) 456-4926</a>
                      </Message.Content>
                    </p>
                  </Message>
                )}
              </div>

              <div className='tw-flex tw-w-full tw-flex-col tw-gap-2 lg:tw-flex-row lg:tw-justify-between'>
                <Button
                  onClick={onReturnToEditStep}
                  className='no-padding-button tw-m-0 tw-flex tw-h-[38px] tw-items-center tw-justify-center tw-gap-2 tw-overflow-hidden tw-whitespace-nowrap'
                >
                  <FontAwesomeIcon icon={faPen} />
                  <span>Back to Edit</span>
                </Button>
                <div className='tw-flex tw-flex-col tw-gap-2 lg:tw-flex-row'>
                  <Button
                    secondary
                    className='tw-m-0 tw-whitespace-nowrap'
                    onClick={handleSubmit(onSaveDraft)}
                    loading={isSaveDraftLoading}
                  >
                    Save to Drafts
                  </Button>
                  {shouldSchedule && (
                    <Button
                      primary
                      className='tw-m-0 tw-whitespace-nowrap'
                      disabled={shouldDisablePublishButtons}
                      loading={isScheduleLoading}
                      onClick={handleSubmit(onSchedule)}
                    >
                      Schedule
                    </Button>
                  )}
                  {shouldSend && (
                    <Button
                      primary
                      className='tw-m-0 tw-whitespace-nowrap'
                      disabled={shouldDisablePublishButtons}
                      loading={isSendLoading}
                      onClick={handleSubmit(onSend)}
                    >
                      Send
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Segment>
    </div>
  );
}

function ReturnToEditButton({ onClick }: { onClick: () => void }) {
  return (
    <Button onClick={onClick} className='no-padding-button tw-mr-0 tw-leading-4'>
      Edit
    </Button>
  );
}
